import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Plus, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import DashboardLayout from "@/components/DashboardLayout";
import { Empty, StatusBadge } from "@/components/Shared";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORY_HINTS = {
    "Payment Issue": "Payment failed, receipt or refund-related query.",
    "Application Problem": "Application status, submission or eligibility problem.",
    "Internship Query": "Questions about program, duration, fee or schedule.",
    "Project / Research Help": "Questions about mentorship, research or project guidance.",
    "Faculty / Mentor Issue": "Problems related to a faculty or mentor interaction.",
    "Account Problem": "Login, profile or account-access issues.",
    "Certificate Issue": "Certificate delivery, correction or verification.",
    "Other": "Anything else that requires assistance.",
};

function SupportContent() {
    const { user } = useAuth();
    const [categories, setCategories] = useState(Object.keys(CATEGORY_HINTS));
    const [tickets, setTickets] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [active, setActive] = useState(null);
    const [reply, setReply] = useState("");
    const [form, setForm] = useState({ category: "Payment Issue", subject: "", message: "" });
    const [busy, setBusy] = useState(false);

    const load = () => {
        if (user) api.get("/tickets").then((r) => setTickets(r.data)).catch(() => setTickets([]));
    };
    useEffect(() => { api.get("/tickets/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);
    useEffect(() => { load(); }, [user]);

    const create = async () => {
        setBusy(true);
        try {
            await api.post("/tickets", form);
            toast.success("Support ticket created!");
            setCreateOpen(false);
            setForm({ category: "Payment Issue", subject: "", message: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const openTicket = async (t) => {
        const { data } = await api.get(`/tickets/${t.id}`);
        setActive(data);
    };

    const sendReply = async () => {
        if (!reply.trim()) return;
        await api.post(`/tickets/${active.id}/messages`, { message: reply });
        setReply("");
        openTicket(active);
    };

    return (
        <div className="mx-auto max-w-5xl" data-testid="support-page">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Help & Support</h1>
                    <p className="mt-2 text-base text-muted-foreground">Every issue becomes a trackable ticket with a full conversation history.</p>
                </div>
                {user && (
                    <Button data-testid="new-ticket-button" className="rounded-full" onClick={() => setCreateOpen(true)}>
                        <Plus className="mr-2 h-4 w-4" /> New Ticket
                    </Button>
                )}
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {categories.map((c) => (
                    <div key={c} className="card-soft p-5" data-testid={`support-category-${c.toLowerCase().replace(/[\s/]+/g, "-")}`}>
                        <LifeBuoy className="h-5 w-5 text-primary" />
                        <p className="mt-3 text-sm font-semibold">{c}</p>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{CATEGORY_HINTS[c]}</p>
                    </div>
                ))}
            </div>

            {user && tickets !== null && (
                <div className="mt-12">
                    <h2 className="font-display text-xl font-semibold">My Tickets</h2>
                    {tickets.length === 0 ? <div className="mt-6"><Empty icon={LifeBuoy} title="No tickets yet" hint="Create a ticket and track the entire conversation here." /></div>
                    : (
                        <div className="mt-6 space-y-4">
                            {tickets.map((t) => (
                                <button key={t.id} onClick={() => openTicket(t)} data-testid={`ticket-${t.ticket_no}`}
                                    className="card-soft flex w-full items-center justify-between gap-4 p-5 text-left transition-transform duration-200 hover:-translate-y-0.5">
                                    <div>
                                        <p className="font-mono text-xs text-muted-foreground">#{t.ticket_no}</p>
                                        <p className="mt-1 font-display text-sm font-semibold">{t.subject}</p>
                                        <p className="text-xs text-muted-foreground">{t.category}</p>
                                    </div>
                                    <StatusBadge status={t.status} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!user && (
                <div className="card-soft mt-12 p-8 text-center">
                    <p className="text-sm text-muted-foreground">Log in to create and track support tickets, or email us at <a href="mailto:support@techbloom.com" className="font-semibold text-primary">support@techbloom.com</a>.</p>
                </div>
            )}

            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent data-testid="create-ticket-dialog">
                    <DialogHeader><DialogTitle>New Support Ticket</DialogTitle></DialogHeader>
                    <div className="space-y-4 pt-2">
                        <div className="space-y-2">
                            <Label>Category</Label>
                            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                <SelectTrigger data-testid="ticket-category-select" className="rounded-lg"><SelectValue /></SelectTrigger>
                                <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="subject">Subject</Label>
                            <Input id="subject" data-testid="ticket-subject-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-lg" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="message">Describe the issue</Label>
                            <Textarea id="message" data-testid="ticket-message-input" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-lg" />
                        </div>
                        <Button data-testid="ticket-submit-button" disabled={busy || !form.subject || !form.message} className="w-full rounded-full" onClick={create}>
                            {busy ? "Creating..." : "Create Ticket"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={!!active} onOpenChange={() => setActive(null)}>
                <DialogContent className="max-w-lg" data-testid="ticket-detail-dialog">
                    {active && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-3">#{active.ticket_no} — {active.subject} <StatusBadge status={active.status} /></DialogTitle>
                            </DialogHeader>
                            <div className="max-h-80 space-y-3 overflow-y-auto pt-2">
                                {active.messages.map((m, i) => (
                                    <div key={i} className={`rounded-xl p-4 text-sm ${m.sender === "admin" ? "bg-primary/5" : "bg-muted"}`} data-testid={`ticket-message-${i}`}>
                                        <p className="text-xs font-semibold text-muted-foreground">{m.sender_name} · {new Date(m.at).toLocaleString()}</p>
                                        <p className="mt-1">{m.text}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 flex gap-2">
                                <Input data-testid="ticket-reply-input" placeholder="Write a reply..." value={reply} onChange={(e) => setReply(e.target.value)} className="rounded-lg" />
                                <Button data-testid="ticket-reply-button" size="icon" onClick={sendReply}><Send className="h-4 w-4" /></Button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function Support() {
    const { user } = useAuth();
    if (user) return <DashboardLayout title="Help & Support"><SupportContent /></DashboardLayout>;
    return (
        <div className="bg-background">
            <Header />
            <div className="px-6 py-14"><SupportContent /></div>
            <Footer />
        </div>
    );
}
